<template>
  <div :id="chartId" :style="chartStyle" class="echarts-dom"></div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "EchartsDom",
  props: {
    // 图表类型：pie(饼图), bar(一维柱状图), bar2(二维柱状图), line(一维曲线图), line2(二维曲线图)
    type: {
      type: String,
      required: true,
      validator: (value) => ["pie", "bar", "bar2", "line", "line2"].includes(value),
    },
    // 图表数据，格式为 [{name: '', value: ''}] 或二维数据
    data: {
      type: Array,
      required: true,
    },
    // 图表标题
    title: {
      type: [String, Object],
      default: "",
    },
    // 图表宽度
    width: {
      type: [String, Number],
      default: "100%",
    },
    // 图表高度
    height: {
      type: [String, Number],
      default: "400px",
    },
    // 自定义样式
    customStyle: {
      type: Object,
      default: () => ({}),
    },
    // 是否显示label
    showLabel: {
      type: Boolean,
      default: true,
    },
    // label位置
    labelPosition: {
      type: String,
      default: "inside",
      validator: (value) =>
        ["top", "bottom", "inside", "left", "right", "center"].includes(value),
    },
    // 自定义label配置对象
    labelOption: {
      type: Object,
      default: () => ({}),
    },
    // 是否显示千分位分隔符
    thousandSeparator: {
      type: Boolean,
      default: true,
    },
    // 是否显示小数位数
    decimalPlaces: {
      type: Number,
      default: 0,
    },

    // 是否显示图例
    showLegend: {
      type: Boolean,
      default: true,
    },
    // 图例位置
    legendPosition: {
      type: String,
      default: "top",
      validator: (value) => ["left", "right", "top", "bottom"].includes(value),
    },
    // 图例距离顶部的高度
    legendTop: {
      type: [String, Number],
      default: "30px",
    },
    // 图例距离左侧的距离
    legendLeft: {
      type: [String, Number],
      default: "20px",
    },
    // 图例距离右侧的距离
    legendRight: {
      type: [String, Number],
      default: "auto",
    },
    // 图例距离底部的距离
    legendBottom: {
      type: [String, Number],
      default: "20px",
    },
    // 图例类型
    legendType: {
      type: String,
      default: "scroll",
      validator: (value) => ["plain", "scroll"].includes(value),
    },
    // 柱状图Y轴名称
    yAxisName: {
      type: String,
      default: "",
    },
    // 柱状图X轴标签旋转角度
    xAxisLabelRotate: {
      type: Number,
      default: 0,
    },
    // 二维柱状图是否堆叠
    isStack: {
      type: Boolean,
      default: true,
    },

    // 柱状图宽度
    barWidth: {
      type: [String, Number],
      default: "auto",
    },
    // 柱状图最小宽度
    barMinWidth: {
      type: [String, Number],
      default: undefined,
    },
    // 柱状图最大宽度
    barMaxWidth: {
      type: [String, Number],
      default: "40px",
    },
    // 网格配置
    grid: {
      type: Object,
      default: () => ({}),
    },
    // X轴数据类型：category(类目轴), value(数值轴)
    xAxisType: {
      type: String,
      default: "category",
      validator: (value) => ["category", "value"].includes(value),
    },
    // Y轴数据类型：category(类目轴), value(数值轴)
    yAxisType: {
      type: String,
      default: "value",
      validator: (value) => ["category", "value"].includes(value),
    },
    // X轴名称
    xAxisName: {
      type: String,
      default: "",
    },
    // 曲线图是否平滑
    smooth: {
      type: Boolean,
      default: false,
    },
    // 曲线图是否显示数据点
    showSymbol: {
      type: Boolean,
      default: true,
    },
    // 曲线图线条宽度
    lineWidth: {
      type: Number,
      default: 2,
    },
    // 曲线图数据点大小
    symbolSize: {
      type: Number,
      default: 4,
    },

    // 二维曲线图是否显示面积
    showArea: {
      type: Boolean,
      default: false,
    },
    // 二维曲线图面积透明度
    areaOpacity: {
      type: Number,
      default: 0.3,
    },
    // 二维曲线图是否显示数据点
    line2ShowSymbol: {
      type: Boolean,
      default: true,
    },
    // 二维曲线图线条宽度
    line2Width: {
      type: Number,
      default: 2,
    },
    // 二维曲线图数据点大小
    line2SymbolSize: {
      type: Number,
      default: 4,
    },
    // 二维曲线图是否平滑
    line2Smooth: {
      type: Boolean,
      default: false,
    },
    // 饼图半径（内半径和外半径）
    pieRadius: {
      type: [String, Number, Array],
      default: "60%",
    },
    // 饼图内半径
    pieInnerRadius: {
      type: [String, Number],
      default: 0,
    },
    // 饼图外半径
    pieOuterRadius: {
      type: [String, Number],
      default: "60%",
    },
    // 饼图中心位置
    pieCenter: {
      type: Array,
      default: () => ["50%", "50%"],
    },
    // 饼图是否显示为环形
    pieDoughnut: {
      type: Boolean,
      default: false,
    },
    // X轴对象配置
    xAxisOption: {
      type: Object,
      default: () => ({}),
    },
    // Y轴对象配置
    yAxisOption: {
      type: Object,
      default: () => ({}),
    },
    // 是否启用一维柱状图自定义每根柱颜色
    isBarColors: {
      type: Boolean,
      default: false,
    },
    // 一维柱状图每个柱子的颜色数组
    barColors: {
      type: Array,
      default: () => [],
    },
    // 一维柱状图每个柱子的默认颜色数组（仅 isBarColors=true 且 barColors 为空时循环使用）
    defaultBarColors: {
      type: Array,
      default: () => [
        "#5470c6",
        "#91cc75",
        "#fac858",
        "#ee6666",
        "#73c0de",
        "#3ba272",
        "#fc8452",
        "#9a60b4",
        "#ea7ccc",
      ],
    },
    // 自定义tooltip配置对象
    tooltipOption: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      chart: null,
      chartId: "",
    };
  },
  computed: {
    chartStyle() {
      return {
        width: typeof this.width === "number" ? `${this.width}px` : this.width,
        height: typeof this.height === "number" ? `${this.height}px` : this.height,
        ...this.customStyle,
      };
    },
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    type() {
      this.updateChart();
    },
  },
  mounted() {
    this.initChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    // 生成唯一ID
    generateId() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = "";
      for (let i = 0; i < 32; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    // 格式化数字，支持千分位分隔符
    formatNumber(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const num = Number(value);
      if (isNaN(num)) {
        return value;
      }
      if (this.thousandSeparator) {
        return this.$tools.formatThousand(num, this.decimalPlaces);
      }
      return num.toString();
    },

    // 初始化图表
    initChart() {
      this.chartId = this.generateId();
      this.$nextTick(() => {
        const dom = document.getElementById(this.chartId);
        if (dom) {
          this.chart = echarts.init(dom);
          this.updateChart();

          // 监听窗口大小变化
          window.addEventListener("resize", this.resizeChart);
        }
      });
    },

    // 更新图表
    updateChart() {
      if (!this.chart) return;

      const option = this.getChartOption();
      this.chart.setOption(option, true);
    },

    // 获取图表配置
    getChartOption() {
      switch (this.type) {
        case "pie":
          return this.getPieOption();
        case "bar":
          return this.getBarOption();
        case "bar2":
          return this.getBar2Option();
        case "line":
          return this.getLineOption();
        case "line2":
          return this.getLine2Option();
        default:
          return {};
      }
    },

    // 饼图配置
    getPieOption() {
      const option = {};
      let titleOption = null;
      if (
        typeof this.title === "object" &&
        this.title !== null &&
        Object.keys(this.title).length > 0
      ) {
        titleOption = { ...this.title };
      } else if (typeof this.title === "string" && this.title.trim() !== "") {
        titleOption = {
          text: this.title,
          left: "center",
        };
      }
      if (titleOption) {
        option.title = titleOption;
      }

      // 检查是否所有数据都是0
      const hasNonZeroData = this.data.some((item) => item.value && item.value !== 0);

      return {
        ...option,
        tooltip: this.getTooltipOption({
          trigger: "item",
          formatter: (params) => {
            return params.value && params.value !== 0
              ? `${params.name}: ${this.formatNumber(params.value)} (${params.percent}%)`
              : "";
          },
          appendToBody: true,
        }),
        legend: {
          show: this.showLegend,
          type: this.legendType,
          orient:
            this.legendPosition === "left" || this.legendPosition === "right"
              ? "vertical"
              : "horizontal",
          left: this.getLegendLeft(),
          right: this.getLegendRight(),
          top: this.getLegendTop(),
          bottom: this.getLegendBottom(),
          textStyle: {
            fontSize: 12,
          },
          itemWidth: 10,
          itemHeight: 10,
        },
        series: [
          {
            name: this.title || "",
            type: "pie",
            radius: this.getPieRadius(),
            center: this.pieCenter,
            data: hasNonZeroData
              ? this.data.map((item) => ({
                  ...item,
                }))
              : [], // 如果所有数据都是0，则传入空数组显示空白饼图
            label: this.getLabelOption("pie"),
            labelLine: {
              show: false,
              length: 0,
              length2: 0,
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: "rgba(0, 0, 0, 0.5)",
              },
            },
          },
        ],
      };
    },

    // 一维柱状图配置
    getBarOption() {
      const xAxisData = this.data.map((item) => item.name);
      const seriesData = this.data.map((item) => item.value);

      const option = {};
      let titleOption = null;
      if (
        typeof this.title === "object" &&
        this.title !== null &&
        Object.keys(this.title).length > 0
      ) {
        titleOption = { ...this.title };
      } else if (typeof this.title === "string" && this.title.trim() !== "") {
        titleOption = {
          text: this.title,
          left: "center",
        };
      }
      if (titleOption) {
        option.title = titleOption;
      }

      return {
        ...option,
        tooltip: this.getTooltipOption({
          trigger: "axis",
          appendToBody: true,
        }),
        grid:
          Object.keys(this.grid).length > 0
            ? this.grid
            : {
                left: 60,
                top: 90,
                right: 40,
                bottom: 40,
              },
        legend: {
          show: false,
        },
        xAxis: {
          type: "category",
          data: xAxisData,
          axisLabel: {
            rotate: this.xAxisLabelRotate,
            interval: 0,
          },
          axisPointer: {
            type: "shadow",
          },
          ...this.xAxisOption,
        },
        yAxis: {
          type: "value",
          name: this.yAxisName,
          minInterval: 1,
          ...this.yAxisOption,
        },
        series: [
          {
            name: this.title,
            type: "bar",
            data: seriesData,
            barWidth: this.barWidth,
            barMinWidth: this.barMinWidth,
            barMaxWidth: this.barMaxWidth,
            itemStyle: {
              // 优先：barColors传了并且有值，则循环使用barColors
              // 其次：当isBarColors为true且barColors为空时，循环使用defaultBarColors
              // 否则：使用ECharts默认配色
              color:
                this.barColors && this.barColors.length > 0
                  ? (params) => this.barColors[params.dataIndex % this.barColors.length]
                  : this.isBarColors
                  ? (params) =>
                      this.defaultBarColors[
                        params.dataIndex % this.defaultBarColors.length
                      ]
                  : undefined,
            },
            label: this.getLabelOption(),
          },
        ],
      };
    },

    // 二维柱状图配置
    getBar2Option() {
      // 二维数据格式：[{name: '系列名', value: [{name: 'x轴', value: 数值}]}]
      const legendData = this.data.map((item) => item.name);
      const xAxisData = this.data[0]?.value?.map((item) => item.name) || [];

      const series = this.data.map((item) => ({
        name: item.name,
        type: "bar",
        data: item.value.map((subItem) => subItem.value),
        barWidth: this.barWidth,
        barMinWidth: this.barMinWidth,
        barMaxWidth: this.barMaxWidth,
        stack: this.isStack ? "total" : null,
        label: this.getLabelOption(),
      }));

      const option = {};
      let titleOption = null;
      if (
        typeof this.title === "object" &&
        this.title !== null &&
        Object.keys(this.title).length > 0
      ) {
        titleOption = { ...this.title };
      } else if (typeof this.title === "string" && this.title.trim() !== "") {
        titleOption = {
          text: this.title,
          left: "center",
        };
      }
      if (titleOption) {
        option.title = titleOption;
      }

      return {
        ...option,
        tooltip: this.getTooltipOption({
          trigger: "axis",
          appendToBody: true,
          axisPointer: {
            type: "shadow",
          },
        }),
        grid:
          Object.keys(this.grid).length > 0
            ? this.grid
            : {
                left: 60,
                top: 90,
                right: 40,
                bottom: 40,
              },
        legend: {
          show: this.showLegend,
          type: this.legendType,
          orient:
            this.legendPosition === "left" || this.legendPosition === "right"
              ? "vertical"
              : "horizontal",
          width: this.legendType === "scroll" ? "80%" : "auto",
          data: legendData,
          left: this.getLegendLeft(),
          right: this.getLegendRight(),
          top: this.getLegendTop(),
          bottom: this.getLegendBottom(),
          textStyle: {
            fontSize: 12,
          },
          itemWidth: 10,
          itemHeight: 10,
        },
        xAxis: {
          type: "category",
          data: xAxisData,
          axisLabel: {
            rotate: this.xAxisLabelRotate,
            interval: 0,
          },
          axisPointer: {
            type: "shadow",
          },
          ...this.xAxisOption,
        },
        yAxis: {
          type: "value",
          name: this.yAxisName,
          // axisLabel: {
          //   formatter: `{value} ${this.yAxisName}`,
          // },
          ...this.yAxisOption,
        },
        series: series,
      };
    },

    // 一维曲线图配置
    getLineOption() {
      // 一维数据格式：[{name: '', value: ''}]
      const xAxisData = this.data.map((item) => item.name);
      const seriesData = this.data.map((item) => item.value);

      // 当启用自定义颜色时，为一维折线图选择颜色
      const lineColor = this.isBarColors
        ? this.barColors && this.barColors.length > 0
          ? this.barColors[0]
          : this.defaultBarColors[0]
        : undefined;

      const option = {};
      let titleOption = null;
      if (
        typeof this.title === "object" &&
        this.title !== null &&
        Object.keys(this.title).length > 0
      ) {
        titleOption = { ...this.title };
      } else if (typeof this.title === "string" && this.title.trim() !== "") {
        titleOption = {
          text: this.title,
          left: "center",
        };
      }
      if (titleOption) {
        option.title = titleOption;
      }

      return {
        ...option,
        tooltip: this.getTooltipOption({
          trigger: "axis",
          appendToBody: true,
          axisPointer: {
            type: "cross",
          },
        }),
        grid:
          Object.keys(this.grid).length > 0
            ? this.grid
            : {
                left: 60,
                top: 90,
                right: 40,
                bottom: 40,
              },
        legend: {
          show: false,
        },
        xAxis: {
          type: this.xAxisType,
          data: this.xAxisType === "category" ? xAxisData : undefined,
          name: this.xAxisName,
          axisLabel: {
            rotate: this.xAxisLabelRotate,
            interval: 0,
          },
          axisPointer: {
            type: "shadow",
          },
          ...this.xAxisOption,
        },
        yAxis: {
          type: this.yAxisType,
          name: this.yAxisName,
          axisLabel: {
            formatter: this.yAxisName ? `{value} ${this.yAxisName}` : undefined,
          },
          minInterval: this.yAxisType === "value" ? 1 : undefined,
          ...this.yAxisOption,
        },
        series: [
          {
            name: this.title || "",
            type: "line",
            data: seriesData,
            smooth: this.smooth,
            symbol: this.showSymbol ? "circle" : "none",
            symbolSize: this.symbolSize,
            lineStyle: {
              width: this.lineWidth,
              color: lineColor,
            },
            itemStyle: lineColor ? { color: lineColor } : {},
            label: this.getLabelOption(),
          },
        ],
      };
    },

    // 二维曲线图配置
    getLine2Option() {
      // 二维数据格式：[{name: '系列名', value: [{name: 'x轴', value: 数值}]}]
      const legendData = this.data.map((item) => item.name);
      const xAxisData = this.data[0]?.value?.map((item) => item.name) || [];

      const series = this.data.map((item) => ({
        name: item.name,
        type: "line",
        data: item.value.map((subItem) => subItem.value),
        smooth: this.line2Smooth,
        symbol: this.line2ShowSymbol ? "circle" : "none",
        symbolSize: this.line2SymbolSize,
        lineStyle: {
          width: this.line2Width,
        },

        areaStyle: this.showArea
          ? {
              opacity: this.areaOpacity,
            }
          : null,
        label: this.getLabelOption(),
      }));

      const option = {};
      let titleOption = null;
      if (
        typeof this.title === "object" &&
        this.title !== null &&
        Object.keys(this.title).length > 0
      ) {
        titleOption = { ...this.title };
      } else if (typeof this.title === "string" && this.title.trim() !== "") {
        titleOption = {
          text: this.title,
          left: "center",
        };
      }
      if (titleOption) {
        option.title = titleOption;
      }

      return {
        ...option,
        tooltip: this.getTooltipOption({
          trigger: "axis",
          appendToBody: true,
          axisPointer: {
            type: "cross",
          },
        }),
        grid:
          Object.keys(this.grid).length > 0
            ? this.grid
            : {
                left: 60,
                top: 90,
                right: 40,
                bottom: 40,
              },
        legend: {
          show: this.showLegend,
          type: this.legendType,
          orient:
            this.legendPosition === "left" || this.legendPosition === "right"
              ? "vertical"
              : "horizontal",
          width: this.legendType === "scroll" ? "80%" : "auto",
          data: legendData,
          left: this.getLegendLeft(),
          right: this.getLegendRight(),
          top: this.getLegendTop(),
          bottom: this.getLegendBottom(),
          textStyle: {
            fontSize: 12,
          },
          itemWidth: 10,
          itemHeight: 10,
        },
        xAxis: {
          type: this.xAxisType,
          data: this.xAxisType === "category" ? xAxisData : undefined,
          name: this.xAxisName,
          axisLabel: {
            rotate: this.xAxisLabelRotate,
            interval: 0,
          },
          axisPointer: {
            type: "shadow",
          },
          ...this.xAxisOption,
        },
        yAxis: {
          type: this.yAxisType,
          name: this.yAxisName,
          axisLabel: {
            formatter: this.yAxisName ? `{value} ${this.yAxisName}` : undefined,
          },
          minInterval: this.yAxisType === "value" ? 1 : undefined,
          ...this.yAxisOption,
        },
        series: series,
      };
    },

    // 图表大小调整
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },

    // 显示加载动画
    showLoading() {
      if (this.chart) {
        this.chart.showLoading();
      }
    },

    // 隐藏加载动画
    hideLoading() {
      if (this.chart) {
        this.chart.hideLoading();
      }
    },

    // 获取图例左侧位置
    getLegendLeft() {
      if (this.legendPosition === "left") {
        return this.legendLeft;
      } else if (this.legendPosition === "right") {
        return "auto";
      } else if (this.legendPosition === "top" || this.legendPosition === "bottom") {
        return "center";
      } else {
        return this.legendLeft;
      }
    },

    // 获取图例右侧位置
    getLegendRight() {
      if (this.legendPosition === "right") {
        return this.legendRight;
      } else if (this.legendPosition === "left") {
        return "auto";
      } else if (this.legendPosition === "top" || this.legendPosition === "bottom") {
        return "center";
      } else {
        return this.legendRight;
      }
    },

    // 获取图例顶部位置
    getLegendTop() {
      if (this.legendPosition === "top") {
        return this.legendTop;
      } else if (this.legendPosition === "bottom") {
        return "auto";
      } else {
        return this.legendTop;
      }
    },

    // 获取图例底部位置
    getLegendBottom() {
      if (this.legendPosition === "bottom") {
        return this.legendBottom;
      } else {
        return "auto";
      }
    },

    // 获取饼图半径配置
    getPieRadius() {
      if (this.pieDoughnut) {
        // 环形饼图：使用内半径和外半径
        return [this.pieInnerRadius, this.pieOuterRadius];
      } else if (Array.isArray(this.pieRadius)) {
        // 如果pieRadius是数组，直接使用
        return this.pieRadius;
      } else {
        // 普通饼图：使用单个半径值
        return this.pieRadius;
      }
    },

    // 获取label配置
    getLabelOption(chartType = "default") {
      // 如果传入了labelOption且不为空，直接使用
      if (
        this.labelOption &&
        typeof this.labelOption === "object" &&
        Object.keys(this.labelOption).length > 0
      ) {
        return { ...this.labelOption };
      }

      // 否则使用默认配置
      if (chartType === "pie") {
        // 饼图默认label配置
        return {
          show: this.showLabel,
          formatter: (params) => {
            return params.value && params.value !== 0
              ? `${params.name}: ${this.formatNumber(params.value)} (${params.percent}%)`
              : "";
          },
        };
      } else {
        // 柱状图和曲线图默认label配置
        return {
          show: this.showLabel,
          position: this.labelPosition,
          formatter: (params) => {
            return params.value && params.value !== 0
              ? this.formatNumber(params.value)
              : "";
          },
        };
      }
    },

    // 获取tooltip配置
    getTooltipOption(defaultTooltip) {
      // 如果传入了tooltipOption且不为空，直接使用
      if (
        this.tooltipOption &&
        typeof this.tooltipOption === "object" &&
        Object.keys(this.tooltipOption).length > 0
      ) {
        return { ...this.tooltipOption };
      }
      // 否则使用默认配置
      return defaultTooltip;
    },
  },
};
</script>

<style lang="scss" scoped>
.echarts-dom {
  width: 100%;
  height: 100%;
}
</style>
